import {useTranslation} from '@aboutyou/i18n';
import {useContactRoute, usePrivacyPolicyRoute} from '@aboutyou/router';
import {useIsPersonalisationConsentGiven} from '@aboutyou/storage';
import {
  COLORS,
  Disclaimer,
  FONT_SIZES,
  FONT_WEIGHTS,
  RadioButton,
  SPACING,
  Text,
  TextInput,
  tabletAndUpLinaria,
} from '@aboutyou/ui-components';
import {useUserEmail} from '@aboutyou/user';
import {emailRegex} from '@aboutyou/utils';
import {styled} from '@linaria/react';
import React, {FC} from 'react';
import {useNewsletterFormHandler} from '../hooks/useNewsletterFormHandler';

interface NewsletterFormProps {
  isUserNotSubscribed: boolean;
  newsletterFormHandler: ReturnType<typeof useNewsletterFormHandler>;
  formId: string;
}

export const NewsletterForm: FC<NewsletterFormProps> = ({
  isUserNotSubscribed,
  newsletterFormHandler,
  formId,
}) => {
  const privacyPolicyRoute = usePrivacyPolicyRoute();
  const contactRoute = useContactRoute();
  const {t} = useTranslation();
  const userEmail = useUserEmail();
  const isPersonalisationConsentGiven = useIsPersonalisationConsentGiven();
  const {register, submitHandler, errors} = newsletterFormHandler;

  return (
    <Form
      data-testid="newsletterForm"
      onSubmit={submitHandler}
      role="form"
      id={formId}
    >
      <div>
        <EmailInput
          type="email"
          autoComplete="email"
          activeStyle={{
            backgroundColor: COLORS.White,
            borderColor: COLORS.Grey3,
          }}
          label={t('Your e-mail')}
          value={isPersonalisationConsentGiven && userEmail ? userEmail : ''}
          errorMessage={errors.email?.message}
          {...register('email', {
            required: true,
            pattern: emailRegex,
          })}
          disabled={!isUserNotSubscribed}
        />
        <GenderSelectorWrapper>
          <GenderLabel size="px14">{t('Receive exclusive offers')}</GenderLabel>
          <GenderOptions>
            <StyledRadioButton
              data-testid="genderRadioButton"
              label={t('For women')}
              value="female"
              {...register('gender', {
                required: true,
              })}
              disabled={!isUserNotSubscribed}
            />
            <StyledRadioButton
              data-testid="genderRadioButton"
              label={t('For men')}
              value="male"
              {...register('gender', {
                required: true,
              })}
              disabled={!isUserNotSubscribed}
            />
          </GenderOptions>
        </GenderSelectorWrapper>
      </div>
      <StyledDisclaimer
        data-testid="disclaimer"
        text={t(
          'I would like to receive the ABOUT YOU newsletter on trends and offers (see {privacyPolicy}) by email. The consent can be withdrawn at any time via a newsletter email or customer service ({helpCenter}).',
          {
            privacyPolicy: `<a rel="noopener noreferrer" target="_blank" href="${privacyPolicyRoute}#section3.3.2.">${t(
              'Privacy Policy',
            )}</a>`,
            helpCenter: `<a rel="noopener noreferrer" target="_blank" href="${contactRoute}">${t(
              'Contact us | ABOUT YOU Help Center',
            )}</a>`,
          },
        )}
      />
    </Form>
  );
};

const GenderSelectorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING['Y.Md']};
  padding: ${SPACING['Y.Lg']} 0;

  [data-is-creative-campaign='true'] & {
    margin-top: 0;
  }
`;

const GenderLabel = styled(Text)`
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: 1.42;

  ${tabletAndUpLinaria(`
    font-weight: ${FONT_WEIGHTS.mediumBold};
  `)}
`;

const GenderOptions = styled.div`
  display: flex;
  gap: 32px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;

  [data-is-creative-campaign='true'] & {
    gap: 16px;
  }
`;

const StyledRadioButton = styled(RadioButton)`
  padding-block: 12px;

  && {
    cursor: default;

    [disabled] + div {
      border-color: ${COLORS.Grey3};

      &::after {
        background-color: ${COLORS.Grey3};
      }
    }
  }
`;

const EmailInput = styled(TextInput)`
  padding: ${SPACING['Y.Lg']} 0;

  & [disabled] {
    border: 1px solid ${COLORS.Grey3};
    color: ${COLORS.Grey3};
  }
`;

const StyledDisclaimer = styled(Disclaimer)`
  font-size: ${FONT_SIZES.px10};
  text-align: left;
  line-height: 1.6;
  color: ${COLORS['Text.Grey']};
`;
